import React, {useState} from 'react';
import EtCelebrateResultIconSVGComp from "@/assets/img/icons/svgComp/EtCelebrateResultIcon1SVGComp";

// 18 Apr 2025
import {
  useGetUserPerformanceLogsQuery,
  useCreateNewUserPerformanceLogMutation,
} from '@/features/userPerformanceLogs/userPerformanceLogsApiSlice';

import { 
  ClockIcon, 
  CalendarDaysIcon, 
  UserGroupIcon,
  ArrowPathIcon,
  DocumentTextIcon,
  ArrowTrendingUpIcon,
  ShareIcon 
} from "@heroicons/react/24/solid"
import PulseLoader from "react-spinners/PulseLoader"

import moment from 'moment';

const EtTrinityWebCelebratePerformanceLog = ({username, userId, userOrgId}) => {
  // Author - Atul Pandey
  // First Created - 09 Apr 2025
  // Purpose to act as the home page for Trinity Landing - Celebrate - Performance Log
  // Updated - 16 Apr 2025 - Updated Header text - See what missions got accomplished! See what mission peaks your efforts lit up!
  // Updated - 18 Apr 2025 - RTK queries and mutations for creating and fetching user performance records
  // Updated - 18 Apr 2025 - JSX display refinements for showing user performance records
  // Updated - 07 Dec 2025 - time chip date fences refined - to align with latest comps such as Reflection Center, Morning Habits

  // 1a - State Variables
  const [activeTab, setActiveTab] = useState('overall'); // 11 Apr 2025 
  const [activePeriod, setActivePeriod] = useState('today'); // 11 Apr 2025


  // 2a - Data - RTK Queries & Mutations
  const {
    data: performanceLogsData,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetUserPerformanceLogsQuery(userId);

  const [createNewPerformanceLog, { isLoading: isCreating }] = useCreateNewUserPerformanceLogMutation();

  // 2b - Select Time period
  //07 Dec 2025
  // --- Time Range Definitions (Aligned with Reflection Center) ---
  const now = moment();
  const startOfToday = moment().startOf("day");
  const endOfToday = moment().endOf("day");

  const startOfWeek = moment().isoWeekday(1).startOf("day");
  const endOfWeek = endOfToday;

  const startOfMonth = moment().startOf("month");
  const endOfMonth = moment().isSame(now, "month")
    ? endOfToday
    : moment().endOf("month");

  const startOfQuarter = moment().startOf("quarter");
  const endOfQuarter = moment().isSame(now, "quarter")
    ? endOfToday
    : moment().endOf("quarter");

  const startOfYear = moment().startOf("year");
  const endOfYear = moment().isSame(now, "year")
    ? endOfToday
    : moment().endOf("year");

  // Generic filter helper
  const filterByTime = (items, start, end) =>
    items.filter(item => {
      const d = moment(item.createdAt);
      return d.isBetween(start, end, null, "[]");
    });

  // const isInSelectedPeriod = (dateString) => {
  //   const now = moment();
  //   const logDate = moment(dateString);
  
  //   switch (activePeriod) {
  //     case 'today':
  //       return logDate.isSame(now, 'day');
  //     case 'week':
  //       return logDate.isSame(now, 'week');
  //     case 'month':
  //       return logDate.isSame(now, 'month');
  //     case 'quarter':
  //       return logDate.isSame(now, 'quarter');
  //     case 'year':
  //       return logDate.isSame(now, 'year');
  //     default:
  //       return true;
  //   }
  // };

  // 2c - Define the logs
  const allLogs = performanceLogsData?.ids?.map(id => performanceLogsData.entities[id]) || [];

  // 07 Dec 2025
  let filteredLogs = [];

  switch (activePeriod) {
    case "today":
      filteredLogs = filterByTime(allLogs, startOfToday, endOfToday);
      break;
    case "week":
      filteredLogs = filterByTime(allLogs, startOfWeek, endOfWeek);
      break;
    case "month":
      filteredLogs = filterByTime(allLogs, startOfMonth, endOfMonth);
      break;
    case "quarter":
      filteredLogs = filterByTime(allLogs, startOfQuarter, endOfQuarter);
      break;
    case "year":
      filteredLogs = filterByTime(allLogs, startOfYear, endOfYear);
      break;
    case "all":
    default:
      filteredLogs = allLogs;
      break;
  }

  // Pick the *latest* performance log in range
  const logs = filteredLogs
    .slice()
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];

  // const logs = allLogs.filter(log => isInSelectedPeriod(log.createdAt))[0];
  
  
  // console.log('all logs', allLogs)
  // console.log('logs', logs)

  return (
    <div className='font-poppins'>
      <div className='m-1 p-1 border-b-4 border-orange-600'>
        <div className='flex justify-start items-center'>
            <div className='flex justify-center items-center w-10 h-10 rounded-full bg-gradient-to-r from-orange-600 to-orange-600 via-orange-800 hover:bg-gradient-to-r hover:from-orange-400 hover:via-orange-600 hover:to-black cursor-pointer shadow-md'>
                <EtCelebrateResultIconSVGComp className="h-7 w-7 text-white" />
            </div>
            <div className='mx-1 px-1 pb-2'>
                {/* <p className="text-md sm:text-xl font-semibold mb-1">Performance Log – The Results</p>
                <p className="text-[10px] sm:text-[13px] text-gray-500">Results from your efforts, categorized by Missions.</p> */}
                <p className="text-md sm:text-lg xl:text-xl font-bold xl:font-semibold mb-1">Performance Log – The Results</p>
                <p className="text-[10px] sm:text-[12px] xl:text-[13px] text-gray-500">See what mission peaks your efforts lit up!</p>
            </div>
        </div>
    </div>
   
    
    {/* Refresh & Tabs */}
    <div className="flex justify-start space-x-4 border-b border-orange-300 mb-4 mt-2">
      {/* Refresh button */}            
      <div className="flex justify-start items-center px-3 py-1"
          onClick={async () => {
            try {
              await createNewPerformanceLog({ userId, userOrgId }).unwrap();
              refetch();
            } catch (err) {
              console.error('Failed to create performance log:', err);
            }
          }}
          disabled={isCreating}
      >
            {isCreating ? (
              <div className='bg-orange-600 p-1 flex justify-start items-center w-48 rounded'>
                <p className='text-white mx-1 px-1'>Syncing</p>
                <PulseLoader color={"#fff"} />
              </div>  
            ) : (
            
              <div className="h-5 w-5 flex justify-center items-center rounded-full bg-gradient-to-r from-orange-600 to-orange-600 via-orange-800 hover:bg-gradient-to-r hover:from-orange-400 hover:via-orange-600 hover:to-black cursor-pointer shadow-md">
                <ArrowPathIcon className="h-4 w-4 text-white" />
              </div>
          )}    
      </div>
      
      {/* Tabs */}
      {['overall', 'induction', 'mymissions'].map(tab => (
        <button
          key={tab}
          className={`text-sm font-semibold px-1 xl:px-3 py-1 rounded-t ${
            activeTab === tab ? 'bg-orange-200 text-orange-800' : 'text-gray-500'
          }`}
          onClick={() => setActiveTab(tab)}
        >
          {tab === 'overall' && 'Overall'}
          {tab === 'induction' && 'Induction'}
          {tab === 'mymissions' && 'MyMissions'}
        </button>
      ))}
    </div>

    {/* Time Filter Chips */}
    <div className="flex flex-wrap gap-1 xl:gap-2 mb-3 text-[9px] xl:text-xs font-semibold">
      {['today', 'week', 'month', 'quarter', 'year'].map(period => (
        <button
          key={period}
          className={`px-2 py-1 rounded-full border ${
            activePeriod === period
              ? 'bg-orange-600 text-white'
              : 'bg-gray-100 text-gray-600'
          }`}
          onClick={() => setActivePeriod(period)}
        >
          {period === 'today' && 'Today'}
          {period === 'week' && 'This Week'}
          {period === 'month' && 'This Month'}
          {period === 'quarter' && 'This Qtr'}
          {period === 'year' && 'This Year'}
        </button>
      ))}
    </div>

      {activeTab === 'overall' && (
        <div className="space-y-1 mt-2 overflow-y-auto max-h-[70vh] rounded bg-gray-200 m-1 p-1 shadow-lg shadow-black">
          <div className="bg-white rounded shadow border border-orange-300 p-4">
            {/* <div className="flex justify-between items-center mb-3"> */}
            <div className="mb-3 pb-2 border-b">
              <h3 className="text-lg font-bold text-orange-800">Your Overall Performance</h3>
              <div className="flex justify-start items-center gap-2 py-1">
                <p className="text-xs text-gray-600">Category:</p>
                <div className="flex justify-center items-center bg-orange-200 px-2 py-[2px] rounded-full text-xs font-semibold text-orange-800">
                  Actions
                </div>
              </div>
            </div>
        
            <div className="grid grid-cols-3 gap-3">
              <div className="flex flex-col items-center justify-center p-2 border border-gray-200 rounded bg-gray-50">
                <p className="text-xs text-gray-600 py-2">Total</p>
                <div className="text-lg font-bold text-gray-800">{logs?.overallPerformance?.totalActions ?? 0}</div>
              </div>
              <div className="flex flex-col items-center justify-center p-2 border border-gray-200 rounded bg-gray-50">
                <p className="text-xs text-gray-600 py-2">Completed</p>
                <div className="text-lg font-bold text-lime-600">{logs?.overallPerformance?.completedActions ?? 0}</div>
              </div>
              <div className="flex flex-col items-center justify-center p-2 border border-gray-200 rounded bg-gray-50">
                <p className="text-xs text-gray-600 py-2">Rate %</p>
                <div className="text-lg font-bold text-indigo-700">
                  {logs?.overallPerformance?.completionRate ?? 0}%
                </div>
              </div>
            </div>
          </div>
        </div>
      
      )}

      {activeTab === 'induction' && (
        <div className="space-y-1 mt-2 overflow-y-auto max-h-[70vh] rounded bg-gray-200 m-1 p-1 shadow-lg shadow-black">
          
          {/* Overview Card */}
          <div className="bg-white rounded shadow border border-orange-400 p-4">
            <div className="mb-2 pb-2 border-b">
              <h3 className="text-md font-bold text-orange-800">Induction Trail Performance</h3>
              <p className="text-xs text-gray-500">Across all Induction trails and sessions</p>
            </div>
            
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="flex flex-col items-center justify-center p-2 border border-gray-200 rounded bg-gray-50">
                <p className="text-xs text-gray-500">Total</p>
                <p className="text-lg font-bold text-gray-700">{logs?.inductionPerformance?.totalActions ?? 0}</p>
              </div>
              <div className="flex flex-col items-center justify-center p-2 border border-gray-200 rounded bg-gray-50">
                <p className="text-xs text-gray-500">Completed</p>
                <p className="text-lg font-bold text-lime-600">{logs?.inductionPerformance?.completedActions ?? 0}</p>
              </div>
              <div className="flex flex-col items-center justify-center p-2 border border-gray-200 rounded bg-gray-50">
                <p className="text-xs text-gray-500">Rate</p>
                <p className="text-lg font-bold text-indigo-700">{logs?.inductionPerformance?.completionRate ?? 0}%</p>
              </div>
            </div>
          </div>

          {/* Trail-Level Breakdown */}
          {logs?.inductionPerformance?.trails?.map((trail, trailIndex) => {
            const trailId = trail?.trailId?._id
            const trailName = trail?.trailId?.trailName
            const trailPic = trail?.trailId?.pic
            
            return(
            <div key={trailId} className="bg-white rounded shadow border border-orange-200 p-3">
              <div className="flex justify-between items-center mb-2 pb-2 border-b">
                <p className="text-xs xl:text-sm font-bold text-orange-700">Trail {trailIndex + 1}</p>
                <div className='flex justify-start items-center'>
                  <img src={trailPic} alt="" className='h-4 w-4'/>
                  <p className='text-orange-600 xl:mx-1 xl:px-1 font-bold text-xs'>{trailName}</p>

                </div>
                <div className="flex justify-end space-x-1 xl:space-x-2">
                  <div className="text-xs text-gray-600">Total: <span className='font-bold mx-1'>{trail.totalActions}</span></div>
                  <div className="text-xs text-lime-600">Done: <span className='font-bold mx-1'>{trail.completedActions}</span></div>
                  <div className="text-xs text-indigo-700">Rate: <span className='font-bold mx-1'>{trail.completionRate}</span>%</div>
                </div>
              </div>

              {/* Session Breakdown */}
              {trail.sessions?.length > 0 && (
                <div className="grid grid-cols-3 gap-2 mt-2 text-[10px] text-gray-700">
                  {trail.sessions.map((session, sIndex) => (
                    <div key={session.sessionId} className="p-2 border rounded bg-gray-50 text-center">
                      <p className="text-[10px] font-semibold text-gray-600">Session {sIndex + 1}</p>
                      <p>Total: {session.totalActions}</p>
                      <p>Done: {session.completedActions}</p>
                      <p>Rate: {session.completionRate}%</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )})}
        </div>
      )}

      {activeTab === 'mymissions' && (
        <div className="space-y-1 mt-2 overflow-y-auto max-h-[70vh] rounded bg-gray-200 m-1 p-1 shadow-lg shadow-black">
          
          {/* Overview Card */}
          <div className="bg-white rounded shadow border border-orange-400 p-4 mb-2">
            <div className="mb-2 pb-2 border-b">
              <h3 className="text-md font-bold text-orange-800">MyMissions Performance</h3>
              <p className="text-xs text-gray-500">Across all focus areas and plans</p>
            </div>

            <div className="grid grid-cols-3 gap-3 text-center">
              <div className='flex flex-col items-center justify-center p-2 border border-gray-200 rounded bg-gray-50'>
                <p className="text-xs text-gray-500 ">Total</p>
                <p className="text-lg font-bold text-gray-700">{logs?.myMissionsPerformance?.totalActions ?? 0}</p>
              </div>
              <div className='flex flex-col items-center justify-center p-2 border border-gray-200 rounded bg-gray-50'>
                <p className="text-xs text-gray-500">Completed</p>
                <p className="text-lg font-bold text-lime-600">{logs?.myMissionsPerformance?.completedActions ?? 0}</p>
              </div>
              <div className='flex flex-col items-center justify-center p-2 border border-gray-200 rounded bg-gray-50'>
                <p className="text-xs text-gray-500">Rate</p>
                <p className="text-lg font-bold text-indigo-700">{logs?.myMissionsPerformance?.completionRate ?? 0}%</p>
              </div>
            </div>
          </div>

          {/* Focus-wise breakdown */}
          {['self', 'client', 'org', 'team', 'partner'].map((focusKey, focusIndex) => {
            const focus = logs?.myMissionsPerformance?.[focusKey];
            if (!focus || focus.totalActions === 0) return null;

            return (
              <div key={focusKey} className="bg-white rounded shadow border border-orange-200 p-3">
                <div className="flex justify-between items-center mb-1">
                  <p className="text-sm font-bold text-orange-700 capitalize">{focusKey}</p>
                  <div className="flex space-x-2">
                    <div className="text-xs text-gray-600">Total: {focus.totalActions}</div>
                    <div className="text-xs text-lime-600">Done: {focus.completedActions}</div>
                    <div className="text-xs text-indigo-700">Rate: {focus.completionRate}%</div>
                  </div>
                </div>

                {/* Mission/Plan Breakdown */}
                {focus.missions?.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2 mt-2 text-[10px] text-gray-700">
                    {focus.missions.map((mission, mIndex) => (
                      <div key={mission.missionId} className="p-2 border rounded bg-gray-50">
                        <p className="font-semibold text-[11px] mb-1">Mission {mIndex + 1}</p>
                        <p>Total: {mission.totalActions}</p>
                        <p>Done: {mission.completedActions}</p>
                        <p>Rate: {mission.completionRate}%</p>

                        {mission.plans?.length > 0 && (
                          <div className="mt-2 border-t pt-1">
                            {mission.plans.map((plan, pIndex) => (
                              <div key={plan.planId} className="text-[10px] mt-1 border-b pb-1">
                                <p className="font-medium">Plan {pIndex + 1}</p>
                                <p>Total: {plan.totalActions}</p>
                                <p>Done: {plan.completedActions}</p>
                                <p>Rate: {plan.completionRate}%</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

    </div>
    
  );
};

export default EtTrinityWebCelebratePerformanceLog;
